/**
 * This logic written specifically for Radar device types
 * Radar currently can be configured only using Synthetic APIs
 * See more here: https://github.com/peoplepower/peoplepower-docs/blob/master/synthetic_apis/radar.md#set-the-room-boundaries
 */
import boundariesTemplate from './boundaries.html';

export default angular
  .module('main.devices.edit.tabs.boundaries', [])

  .directive('editDeviceBoundariesTab', ($filter) => {
    const $t = $filter('translate');
    return {
      restrict: 'E',
      template: boundariesTemplate,
      scope: {},
      controller: (
        $scope,
        $q,
        utils,
        EditDeviceDataProvider,
        ToastsService,
        I18nService,
        $log
      ) => {
        $scope.locale = I18nService.getLocale();
        $scope.dataProvider = EditDeviceDataProvider.instance;
        $scope.errorMessage = '';
        $scope.defaultMountingType = undefined;
        $scope.roomProps = {};
        $scope.empty = true;

        let PROPS_MAPPING = [];

        // Define mounting types for Pontosense
        // NOTE: This is temporary hardcoded solution (switch to device model info later)
        const PONTOSENSE_MOUNTING_TYPES = [
          {
            id: 4,
            mlName: {
              en: 'On the Corner',
              fr: 'Au coin de la rue'
            },
            value: '4'
          }
        ];

        const MOUNTING_TYPES = {
          0: 'wall',
          3: 'wall45',
          1: 'ceiling',
          2: 'ceiling45',
          4: 'corner'
        };

        const DEVICE_BRANDS = {
          2000: 'vayyar',
          2007: 'pontosense',
          2020: 'aerosense',
        };

        /**
         * Radar mounting type
         * @type {'wall' | 'wall45' | 'ceiling' | 'ceiling45' | 'corner'}
         */
        $scope.mountingType = undefined;

        /**
         * Device brand name
         * @type {'vayyar' | 'pontosense' | 'aerosense'}
         */
        $scope.deviceBrand = undefined;

        /**
         * Configuration for form validation
         * NOTE: This is temporary hardcoded solution (switch to parameters)
         * minValue and maxValue are part of the device parameters definition (globally)
         * @type {
         *   {
         *     [mountingSchema: string]: {
         *       xMin: {min: number, max: number, default: number, hidden?: boolean},
         *       xMax: {min: number, max: number, default: number, hidden?: boolean},
         *       yMin: {min: number, max: number, default: number, hidden?: boolean},
         *       yMax: {min: number, max: number, default: number, hidden?: boolean},
         *       height: {min: number, max: number, default: number, hidden?: boolean},
         *       xLength: {min: number, max: number},
         *       yLength: {min: number, max: number},
         *     }
         *   }
         * }
         */
        const FORM_CONFIG = {
          'vayyar-wall': {
            xMin: {
              min: -3.0,
              max: 0.0,
              default: -2
            },
            xMax: {
              min: 0.0,
              max: 2.0,
              default: 2
            },
            xLength: { // xMax - xMin
              min: 0.5,
              max: 4.0
            },
            yMin: {
              min: 0.3,
              max: 4.0,
              default: 0.3
            },
            yMax: {
              min: 0.3,
              max: 4.0,
              default: 2
            },
            yLength: { // yMax - yMin
              min: 0.5,
              max: 3.7
            },
            sensorHeight: {
              min: 1.5,
              max: 1.5,
              default: 1.5,
              hidden: true,
            },
          },
          'vayyar-wall45': {
            xMin: {
              min: -3.0,
              max: 0.0,
              default: -2.0
            },
            xMax: {
              min: 0.0,
              max: 2.0,
              default: 2.0
            },
            xLength: { // xMax - xMin
              min: 0.1,
              max: 4.0
            },
            yMin: {
              min: 0.3,
              max: 4.0,
              default: 0.3
            },
            yMax: {
              min: 0.3,
              max: 4.0,
              default: 2.0
            },
            yLength: { // yMax - yMin
              min: 0.1,
              max: 3.7
            },
            sensorHeight: {
              min: 2.1,
              max: 2.4,
              default: 2.1
            },
          },
          'vayyar-ceiling': {
            xMin: {
              min: -2.5,
              max: 0.0,
              default: -2.0
            },
            xMax: {
              min: 0.0,
              max: 2.5,
              default: 2.0
            },
            xLength: { // xMax - xMin
              min: 0.1,
              max: 5.0
            },
            yMin: {
              min: -3.0,
              max: 0.0,
              default: -2.0
            },
            yMax: {
              min: 0.0,
              max: 3.0,
              default: 2.0
            },
            yLength: { // yMax - yMin
              min: 0.1,
              max: 5.0
            },
            sensorHeight: {
              min: 2.3,
              max: 3.0,
              default: 2.3
            },
          },
          'vayyar-ceiling45': {
            xMin: {
              min: -2.5,
              max: 0.0,
              default: -2.0
            },
            xMax: {
              min: 0.0,
              max: 2.5,
              default: 2.0
            },
            xLength: { // xMax - xMin
              min: 0.1,
              max: 5.0
            },
            yMin: {
              min: -3.0,
              max: 0.0,
              default: -2.0
            },
            yMax: {
              min: 0.0,
              max: 3.0,
              default: 2.0
            },
            yLength: { // yMax - yMin
              min: 0.1,
              max: 5.0
            },
            sensorHeight: {
              min: 2.3,
              max: 3.0,
              default: 2.3
            },
          },
          'pontosense-corner': {
            xMin: {
              min: 0.0,
              max: 0.0,
              default: 0.0,
              hidden: true,
            },
            xMax: {
              min: 0.0,
              max: 6.0,
              default: 2.5
            },
            xLength: { // xMax - xMin
              min: 0.1,
              max: 6.0
            },
            yMin: {
              min: 0.0,
              max: 0.0,
              default: 0.0,
              hidden: true,
            },
            yMax: {
              min: 0.0,
              max: 6.0,
              default: 2.5
            },
            yLength: { // yMax - yMin
              min: 0.1,
              max: 6.0
            },
            sensorHeight: {
              min: 2.0,
              max: 3.5,
              default: 2.0,
            },
          },
          'aerosense-corner': {
            xMin: {
              min: 0.0,
              max: 0.0,
              default: 0.0
            },
            xMax: {
              min: 0.0,
              max: 6.0,
              default: 2.5
            },
            xLength: { // xMax - xMin
              min: 0.1,
              max: 6.0
            },
            yMin: {
              min: 0.0,
              max: 0.0,
              default: 0.0
            },
            yMax: {
              min: 0.0,
              max: 6.0,
              default: 2.5
            },
            yLength: { // yMax - yMin
              min: 0.1,
              max: 6.0
            },
            sensorHeight: {
              min: 2.0,
              max: 3.5,
              default: 2.0,
            },
          }
        };

        // Properties with device parameter pairs
        // NOTE: This is temporary hardcoded solution (switch to device model info later)
        const VAYYAR_PROPS_MAPPING = [
          {
            id: 'mounting',
            deviceParam: 'vyrc.sensorMounting',
            botProperty: 'mounting_type',
            desc: $t('regions.mounting.desc'),
            options: true
          },
          {
            id: 'xMin',
            deviceParam: 'vyrc.xMin',
            botProperty: 'x_min_meters',
            altBotProperty: 'x_min'
          },
          {
            id: 'xMax',
            deviceParam: 'vyrc.xMax',
            botProperty: 'x_max_meters',
            altBotProperty: 'x_max'
          },
          {
            id: 'yMin',
            deviceParam: 'vyrc.yMin',
            botProperty: 'y_min_meters',
            altBotProperty: 'y_min'
          },
          {
            id: 'yMax',
            deviceParam: 'vyrc.yMax',
            botProperty: 'y_max_meters',
            altBotProperty: 'y_max'
          },
          {
            id: 'sensorHeight',
            deviceParam: 'vyrc.sensorHeight',
            botProperty: 'sensor_height_m'
          },
          {
            id: 'nearExit',
            botProperty: 'near_exit',
            title: $t('regions.boundaries.nearExit.title'),
            desc: $t('regions.boundaries.nearExit.desc')
          },
        ];

        // Properties with device parameter pairs
        // NOTE: This is temporary hardcoded solution (switch to device model info later)
        const PONTOSENSE_PROPS_MAPPING = [
          {
            id: 'mounting',
            botProperty: 'mounting_type',
            title: $t('regions.mounting.title'),
            desc: $t('regions.mounting.desc'),
            options: true,
          },
          {
            id: 'xMax',
            deviceParam: 'pnt.scanLeft',
            deviceParamUnits: 'cm',
            botProperty: 'x_max_meters'
          },
          {
            id: 'yMax',
            deviceParam: 'pnt.scanRight',
            deviceParamUnits: 'cm',
            botProperty: 'y_max_meters'
          },
          {
            id: 'sensorHeight',
            deviceParam: 'pnt.scanHeight',
            deviceParamUnits: 'cm',
            botProperty: 'sensor_height_m'
          },
        ];

        angular.extend($scope, {
          onCancelButtonClick: onCancelButtonClick,
          onSaveButtonClick: onSaveButtonClick,
          switchOption: switchOption,
          isEmpty: isEmpty,
          convertToOppositeUnits: convertToOppositeUnits,
        });

        let initial = true;

        init();

        function init() {
          $scope.loading = true;
          initFormRules();

          $q.all([
            $scope.dataProvider.deviceResource.init(),
            $scope.dataProvider.botDrivenRegionsResource.init(),
            $scope.dataProvider.deviceCurrentMeasurementsResource.init(),
            $scope.dataProvider.deviceParametersResource.init(),
            $scope.dataProvider.deviceModelResource.init(),
            $scope.dataProvider.locationStateBoundariesResource.init(),
            $scope.dataProvider.userSystemOfMeasurementResource.init(),
          ])
            .then(() => {
              $scope.systemOfMeasurement = $scope.dataProvider.userSystemOfMeasurementResource.value;

              // Detect device type for initial configuration
              // NOTE: This is temporary hardcoded solution (switch to device model info later)
              $scope.deviceType = $scope.dataProvider.deviceResource.value?.type;

              $scope.deviceBrand = DEVICE_BRANDS[$scope.deviceType];

              PROPS_MAPPING = $scope.deviceBrand === 'vayyar'
                ? VAYYAR_PROPS_MAPPING
                : PONTOSENSE_PROPS_MAPPING;

              processProps();
              initWatchers();
            }, (err) => {
              $scope.loading = false;
              $scope.editable = false;
            });
        }

        function initWatchers() {
          $scope.$watchGroup([
            'dataProvider.botDrivenRegionsResource.value',
            'dataProvider.deviceCurrentMeasurementsResource.value',
            'dataProvider.locationStateBoundariesResource.value'
          ], () => {
            initViewModel();
          });

          // Watch for mounting type changes only for Vayyar devices
          $scope.$watch('selectedMountingId', (newVal, oldVal) => {
            if (oldVal != null && newVal !== oldVal) { // skip first call
              $scope.mountingType = MOUNTING_TYPES[newVal];
              $scope.formConfig = FORM_CONFIG[$scope.deviceBrand + '-' + $scope.mountingType];
              $scope.boundaries.mounting = $scope.selectedMountingId;

              // Recreate form validation rules
              processFormRules();
              $('#room-boundaries-form').form('validate form');
            }
          });

          [
            'xMin',
            'xMax',
            'yMin',
            'yMax',
            'sensorHeight',
          ]
            .forEach((fieldName) => {
              $scope.$watch(fieldName, () => {
                if ($scope.boundaries && $scope[fieldName] != null) {
                  $scope.boundaries[fieldName] = convertFromUserUnits($scope[fieldName]);
                }
              });
            });

          // Build room object for schema
          $scope.$watch('boundaries', () => {
            if (!$scope.boundaries) {
              return;
            }

            $scope.boundariesForSchema = angular.copy($scope.boundaries);

            Object.keys($scope.boundariesForSchema)
              .map(key => {
                let mapping = PROPS_MAPPING.find(el => el.id === key);
                if (mapping && mapping.botProperty !== key) {
                  $scope.boundariesForSchema[mapping.botProperty] = $scope.boundariesForSchema[key];
                  delete $scope.boundariesForSchema[key];
                }
              });
          }, true);
        }

        /**
         * Main initialization
         */
        function initViewModel() {
          let deviceMeasurements = angular.copy($scope.dataProvider.deviceCurrentMeasurementsResource.value?.parameters) || [];

          // Do not allow to edit boundaries if no device parameters returned
          if (!deviceMeasurements || deviceMeasurements.length === 0) {
            $scope.errorMessage = $t('regions.errors.noParameters');
            $scope.editable = false;
            $scope.loading = false;
            return;
          }

          if (initial || !$scope.boundaries) {
            $scope.boundaries = processBoundaries(deviceMeasurements);
            initial = false;
          }

          $scope.selectedMountingId = !isNaN($scope.boundaries['mounting'])
            ? $scope.boundaries['mounting']
            : angular.copy($scope.defaultMountingType);

          $scope.mountingType = MOUNTING_TYPES[$scope.selectedMountingId];
          $scope.editable = $scope.dataProvider.deviceResource.value.connected && $scope.dataProvider.botDrivenRegionsResource.value;

          $scope.xMin = convertToUserUnits($scope.boundaries.xMin);
          $scope.xMax = convertToUserUnits($scope.boundaries.xMax);
          $scope.yMin = convertToUserUnits($scope.boundaries.yMin);
          $scope.yMax = convertToUserUnits($scope.boundaries.yMax);

          $scope.sensorHeight = convertToUserUnits($scope.boundaries.sensorHeight);

          $scope.formConfig = FORM_CONFIG[$scope.deviceBrand + '-' + $scope.mountingType];
          if (!$scope.formConfig) {
            $log.error('Unknown device installation schema: ' + $scope.deviceBrand + ', ' + $scope.mountingType);
            $scope.editable = false;
            return;
          }

          // Apply default values for empty form
          [
            'xMin',
            'xMax',
            'yMin',
            'yMax',
            'sensorHeight',
          ]
            .forEach((field) => {
              if ($scope[field] == null && $scope.formConfig[field].default != null) {
                $scope[field] = $scope.formConfig[field].default;
              }
            });

          // Initialize form validation
          initForm();

          $scope.loading = false;
        }

        function initFormRules() {
          $.fn.form.settings.rules.numberBetween = (value, n) => {
            let flag = $.fn.form.settings.rules.number(value);
            if (flag) {
              if (n) {
                let arr = n.split('..');
                let min = arr.length >= 1 ? Number(arr[0]) : Number.MIN_VALUE,
                  max = arr.length >= 2 ? Number(arr[1]) : Number.MAX_VALUE,
                  valNum = Number(value) && parseFloat(value);
                return valNum >= min & valNum <= max;
              } else {
                return true;
              }
            }
            return false;
          };

          $.fn.form.settings.rules.roomBoundariesX = () => {
            if ($scope.xMin != null && $scope.xMax != null) {
              const xLength = $scope.xMax - $scope.xMin;
              if (xLength < convertToUserUnits($scope.formConfig.xLength.min) || xLength > convertToUserUnits($scope.formConfig.xLength.max)) {
                return false;
              }
            }
            return true;
          };

          $.fn.form.settings.rules.roomBoundariesY = () => {
            if ($scope.yMin != null && $scope.yMax != null) {
              const yLength = $scope.yMax - $scope.yMin;
              if (yLength < convertToUserUnits($scope.formConfig.yLength.min) || yLength > convertToUserUnits($scope.formConfig.yLength.max)) {
                return false;
              }
            }
            return true;
          };
        }

        /**
         * Initial form rules.
         * Process minimum set of form rules for all device types.
         */
        function initForm() {
          $('#room-boundaries-form').form({
            inline: true,
            fields: {}
          });
          processFormRules();
        }

        /**
         * Process rules every time we have to change them.
         * Should be updated every time we're switching from Wall to Ceiling mounting.
         */
        function processFormRules() {
          const $form = $('#room-boundaries-form');
          [
            'xMin',
            'xMax',
            'yMin',
            'yMax',
            'sensorHeight',
          ]
            .forEach((field) => {
              // Clear already added rules
              // NOTE: semantic forms has a bug, it can't remove field with empty ruleset for some reason
              //  so we need to do a hack:
              $form.form('add rule', field, ['empty']);
              $form.form('remove fields', [field]); // remove fields actually removes only validation rules

              const formFieldCfg = $scope.formConfig[field];

              if (!formFieldCfg.hidden) {
                const rules = [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  },

                  // Apply min/max limits
                  {
                    type: `numberBetween[${convertToUserUnits(formFieldCfg.min)}..${convertToUserUnits(formFieldCfg.max)}]`,
                    prompt: $t('regions.boundaries.prompt.between', {
                      min: convertToUserUnits(formFieldCfg.min).toFixed(1),
                      max: convertToUserUnits(formFieldCfg.max).toFixed(1)
                    })
                  }
                ];

                // Apply total room length / width limits
                if (field === 'xMax' || field === 'xMin') {
                  rules.push({
                    type: `roomBoundariesX`,
                    prompt: $t('regions.boundaries.prompt.xLimit', {
                      min: convertToUserUnits($scope.formConfig.xLength.min).toFixed(1),
                      max: convertToUserUnits($scope.formConfig.xLength.max).toFixed(1)
                    })
                  });
                } else if (field === 'yMax' || field === 'yMin') {
                  rules.push({
                    type: `roomBoundariesY`,
                    prompt: $t('regions.boundaries.prompt.yLimit', {
                      min: convertToUserUnits($scope.formConfig.yLength.min).toFixed(1),
                      max: convertToUserUnits($scope.formConfig.yLength.max).toFixed(1)
                    })
                  });
                }

                $form.form('add rule', field, { rules: rules });
              }
            });
        }

        /**
         * Get room boundaries data from location state or from device parameters (if state is empty)
         * @param {{name: string, value: string}[]} deviceMeasurements
         * @returns {Object}
         */
        function processBoundaries(deviceMeasurements) {
          let boundaries = angular.copy($scope.dataProvider.locationStateBoundariesResource.value);

          // If no boundaries returned from location state
          if (!boundaries || Object.keys(boundaries).length === 0) {
            boundaries = {};
            PROPS_MAPPING.forEach(el => {
              let parameter = deviceMeasurements.find(p => p.name === el.deviceParam);

              // Convert value from device parameter units to meters
              if (parameter && el.deviceParamUnits === 'cm') {
                parameter.value = parameter.value / 100;
              }

              boundaries[el.id] = parameter ? parseFloat(parameter.value) : undefined;
            });
          } else {
            Object.keys(boundaries)
              .map(key => {
                let mapping = PROPS_MAPPING.find(el =>
                  el.botProperty === key || el.altBotProperty === key
                );
                if (mapping && mapping.id !== key) {
                  boundaries[mapping.id] = boundaries[key];
                  delete boundaries[key];
                }
              });
          }

          return boundaries;
        }

        /**
         * Enrich mapped properties with device parameters data
         * If parameter will be changed, we'll update property dynamically
         */
        function processProps() {
          let deviceParams = angular.copy($scope.dataProvider.deviceParametersResource.value) || [];

          PROPS_MAPPING.forEach((property) => {
            let param = deviceParams.find(el => el.name === property.deviceParam);
            let result = {
              botProperty: property.botProperty,
              title: param ? param.displayInfo?.mlName[$scope.locale.code] : (property.title || property.id),
              desc: property.desc || param.description
            };

            if (property.options && param) {
              result.options = param.displayInfo?.options?.map(option => {
                option.name = option.mlName ? option.mlName[$scope.locale.code] : undefined;
                return option;
              }) || [];

              // Define mounting options for Vayyar
              if (property.id === 'mounting') {
                result.options = result.options.map(option => {
                  option.value = parseInt(option.value);
                  return option;
                });
                let defaultOption = result.options.find((o) => o.id === param.displayInfo?.defaultOption);
                $scope.defaultMountingType = defaultOption ? defaultOption.value : result.options[0].value;
              }
            } else {

              // Define Pontosense mounting type which is not binded to any device parameter
              // NOTE: This is temporary hardcoded solution (switch to device model info later)
              if (!property.deviceParam || !param) {
                result.options = PONTOSENSE_MOUNTING_TYPES.map(option => {
                  option.name = option.mlName ? option.mlName[$scope.locale.code] : undefined;
                  return option;
                });
                $scope.defaultMountingType = result.options[0].value;
              }
            }

            $scope.roomProps[property.id] = result;
          });

        }

        /**
         * Toggle switch function.
         * @param {string} id
         */
        function switchOption(id) {
          if (!id) {
            return;
          }
          $scope.boundaries[id] = !$scope.boundaries[id];
        }

        function onSaveButtonClick() {
          $('#room-boundaries-form').form('validate form');
          if (!$('#room-boundaries-form').form('is valid')) {
            return;
          }

          $scope.editable = false;
          const data = angular.copy($scope.boundaries);
          data.mounting = angular.copy($scope.selectedMountingId);

          Object.keys(data)
            .map(key => {
              let mapping = PROPS_MAPPING.find(el => el.id === key);
              if (mapping && mapping.botProperty !== key) {
                data[mapping.botProperty] = data[key];
                delete data[key];
              }
            });

          return $scope.dataProvider.saveRoomBoundaries(data)
            .then(() => {
              ToastsService.success();
              $scope.dataProvider.activeTab = 'subregions';
            }, (err) => {
              $scope.editable = true;
              utils.handleApiError(err);
            });
        }

        function onCancelButtonClick() {
          $scope.dataProvider.activeTab = 'subregions';
        }

        /**
         * Checking form fields emptiness.
         * @returns boolean
         */
        function isEmpty() {
          if (!$scope.boundaries) {
            return true;
          }

          let result = false;
          [
            'xMin',
            'xMax',
            'yMin',
            'yMax',
            'sensorHeight'
          ]
            .forEach((field) => {
              if (!$scope.formConfig[field].hidden && $scope.boundaries[field] == null) {
                result = true;
              }
            });
          return result;
        }

        /**
         * Convert meters to user units (centimeters or inches)
         * @param {number} meters
         * @returns {number}
         */
        function convertToUserUnits(meters) {
          if ($scope.systemOfMeasurement === 'metric') {
            return Math.round(meters * 1000) / 10; // From meters to centimeters
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertMetersToInches(meters); // From meters to inches
          }
          return meters;
        }

        /**
         * Convert user units (centimeters or inches) to meters
         * @param {number} usersUnits
         * @returns {number}
         */
        function convertFromUserUnits(usersUnits) {
          if ($scope.systemOfMeasurement === 'metric') {
            return usersUnits / 100; // from centimeters to meters
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertInchesToMeters(usersUnits, 4); // from inches to meters
          }
          return usersUnits;
        }

        /**
         * Convert value to the opposite units of measurement and format value
         * @param {number} value value in current user units of measurement (centimeters or inches)
         * @returns {string} value in opposite user units of measurement (inches or centimeters)
         */
        function convertToOppositeUnits(value) {
          if ($scope.systemOfMeasurement === 'metric') {
            return utils.convertMetersToInches(value / 100) + ' in'; // from centimeters to inches
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertInchesToMeters(value * 100, 2) + ' cm'; // from inches to centimeters
          }
          return value;
        }

      }
    };
  })
  .name;
